import { apiClient, ApiResponse } from './client'
import type { AiqIndividualReportResponse } from '@quebon/shared-learning'

// Organization report types (reuses class report DTO structure)
export interface AiqOrgReportResponse {
  classSummary: {
    totalStudents: number
    completedBothCount: number
    avgPreScore: number
    avgPostScore: number
    avgImprovement: number
    maxImprovement: number
    minImprovement: number
  }
  domainAnalysis: {
    domain: string
    preCorrect: number
    postCorrect: number
    totalQuestions: number
    preAccuracy: number
    postAccuracy: number
    preAvgTimeSec: number
    postAvgTimeSec: number
    preScoreSimple: number
    postScoreSimple: number
    preScoreAdvanced: number | null
    postScoreAdvanced: number | null
  }[]
  studentSummaries: {
    userId: number
    userName: string | null
    email: string | null
    preScore: number | null
    postScore: number | null
    improvement: number | null
    preCompletedAt: string | null
    postCompletedAt: string | null
    status: string
  }[]
  improvementDistribution: {
    rangeLabel: string
    rangeMin: number
    rangeMax: number
    count: number
  }[]
}

/** 능력 유형(안내 4단계): 강점 · 정착 · 성장 · 보완 */
export type AiqAbilityType = 'STRENGTH' | 'SETTLED' | 'GROWING' | 'NEEDS_WORK'

/** 테스트 전체 풀이 속도 4단계 */
export type AiqSpeedBand = 'VERY_FAST' | 'FAST' | 'SLOW' | 'VERY_SLOW'

export interface AiqDomainScore {
  domain: 'abstraction' | 'change' | 'relation'
  score: number | null
  sameAgeScore: number
  quebonScore: number
  content: string
  /** 이 능력으로 분류된 문항 중 정답 수 */
  correctCount?: number
  /** 이 능력으로 분류된 응답 문항 수 */
  itemCount?: number
  /** 능력 유형. 능력 문항이 없으면 null */
  abilityType?: AiqAbilityType | null
}

export interface AiqDiagnosticReportResponse {
  nickName: string
  ageLabel: string
  gradeLabel: string
  diagnosedAt: string | null
  aiqScore: number
  aiqRank: number
  sameAgeAiqScore: number
  quebonAiqScore: number
  domainScores: AiqDomainScore[]
  /** 맞힌 문항이 5개 미만이면 null. 화면에는 아직 쓰지 않는다 */
  speedBand?: AiqSpeedBand | null
}

export const aiqApi = {
  /**
   * Get individual AIQ comparison report (Pre vs Post)
   */
  getIndividualReport: (productId: number, classProductId?: number) => {
    const params = new URLSearchParams({ productId: String(productId) })
    if (classProductId) params.append('classProductId', String(classProductId))
    return apiClient.get<ApiResponse<AiqIndividualReportResponse>>(`/aiq/report/individual?${params}`)
  },

  /**
   * Get organization-level AIQ report (admin)
   */
  getOrganizationReport: (organizationId: number, productId: number) => {
    const params = new URLSearchParams({
      organizationId: String(organizationId),
      productId: String(productId),
    })
    return apiClient.get<ApiResponse<AiqOrgReportResponse>>(`/aiq/report/organization?${params}`)
  },

  /** 학생별 AIQ 진단 리포트 (원장 상담용, admin) */
  getDiagnosticReport: (userId: number, assignmentId?: string) =>
    apiClient.get<ApiResponse<AiqDiagnosticReportResponse>>(`/lc/admin/students/${userId}/aiq-report`, {
      params: assignmentId ? { assignmentId } : undefined,
    }),
}
